import { useState, useContext } from 'react';
import { useSlateStatic } from 'slate-react';
import { useTagDisplay, useTagSelection } from 'app/state/contexts/TagContext';
import { SidebarContext } from 'app/state/contexts/SidebarContext';
import { useEditorMode } from 'app/state/contexts/EditorContext';
import { navigateToTag } from '../utils/navigation';
import TagDotIndicators from './TagDotIndicators';
import { TagBinding } from '../types/block';

interface BlockTagChromeProps {
  bindings: TagBinding[];
  indicatorColor: string;
}

export const BlockTagChrome = ({ bindings, indicatorColor }: BlockTagChromeProps) => {
  const editor = useSlateStatic();
  const { tagDataMap, highlightedOption } = useTagDisplay();
  const { setClickedTagIds, clearBlockSelection } = useTagSelection();
  const { setIsSidebarOpen } = useContext(SidebarContext);
  const { taggingSubMode } = useEditorMode();
  const isCoverageMode = taggingSubMode === 'mark-coverage';
  const [hover, setHover] = useState(false);

  const firstBinding = bindings[0];
  const firstTagData = firstBinding ? tagDataMap?.[firstBinding.tagId] : undefined;
  const isContinuation = !!firstTagData?.predecessorId;
  const hasContinuation = !!firstBinding?.continuedAt || !!firstTagData?.nextSegment;
  const canContinue = firstTagData?.tagType !== 'nonFraction';

  const capBase: React.CSSProperties = {
    position: 'absolute',
    height: '1.15em',
    backgroundColor: indicatorColor,
    padding: '0 2px',
    minWidth: '1px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const allTagIds = bindings.map((b) => b.tagId);

  return (
    <>
      {/* Multiple whole-element tags → colored dots at the left edge (same as inline). */}
      <TagDotIndicators
        allTagIds={allTagIds}
        highlightedOption={highlightedOption}
        tagDataMap={tagDataMap ?? {}}
        onDotClick={(tagId) => {
          if (!tagId || isCoverageMode) return;
          clearBlockSelection?.();
          setClickedTagIds?.([tagId]);
          setIsSidebarOpen?.(8);
        }}
        activeTagId={null}
        indicatorHeight={null}
      />

      <span
        contentEditable={false}
        style={{ ...capBase, top: 0, left: '-5px', borderRadius: '13px 0 0 13px', cursor: isContinuation && !isCoverageMode ? 'pointer' : 'default' }}
        onClick={(e) => {
          if (isCoverageMode) return;
          if (isContinuation && firstTagData?.predecessorId) {
            e.preventDefault();
            e.stopPropagation();
            navigateToTag(editor, firstTagData.predecessorId);
          }
        }}
      >
        {isContinuation ? <i className="fa-solid fa-arrow-left" style={{ fontFamily: '"Font Awesome 6 Pro"', fontSize: '8px', color: '#fff' }} /> : null}
      </span>
      <span
        contentEditable={false}
        style={{
          ...capBase,
          bottom: 0,
          right: '-4px',
          borderRadius: '0 13px 13px 0',
          cursor: isCoverageMode ? 'default' : 'pointer',
          transition: 'width 0.3s ease-in-out, padding 0.3s ease-in-out',
        }}
        onMouseEnter={() => !isCoverageMode && setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => {
          e.preventDefault();
          if (isCoverageMode) return;
          if (canContinue && firstBinding) {
            clearBlockSelection?.();
            setClickedTagIds?.([firstBinding.tagId]);
            setIsSidebarOpen?.(8);
          }
        }}
      >
        {hasContinuation ? (
          <i className="fa-solid fa-arrow-right" style={{ fontFamily: '"Font Awesome 6 Pro"', fontSize: '8px', color: '#fff' }} />
        ) : hover && canContinue ? (
          <i className="fa-regular fa-plus fa-xs" style={{ fontFamily: '"Font Awesome 6 Pro"', fontSize: '8px', color: '#fff' }} />
        ) : null}
      </span>
    </>
  );
};
